import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { authApi, userApi } from '../utils/api';
import { User, Lock, Phone, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Profile() {
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [activeTab, setActiveTab] = useState<'info' | 'password'>('info');

  const updateMutation = useMutation({
    mutationFn: () => userApi.update(user!.id, { name, phone }),
    onSuccess: () => {
      updateUser({ name, phone });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('個人資料已更新');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '更新失敗');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: () => authApi.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      toast.success('密碼已更改');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '密碼更改失敗');
    },
  });

  const handleUpdateInfo = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate();
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('新密碼與確認密碼不符');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('新密碼至少需要6個字元');
      return;
    }
    changePasswordMutation.mutate();
  };

  const getRoleBadge = () => {
    switch (user?.role) {
      case 'admin':
        return { text: '管理員', class: 'bg-rose-100 text-rose-700' };
      case 'teacher':
        return { text: '老師', class: 'bg-amber-100 text-amber-700' };
      default:
        return { text: '學生', class: 'bg-blue-100 text-blue-700' };
    }
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="max-w-lg mx-auto">
      {/* User Info Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-purple-700 text-white shadow-xl mb-6">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20 blur-2xl" />
        <div className="relative p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
              {user?.name?.[0] || '?'}
            </div>
            <div>
              <h2 className="text-xl font-bold">{user?.name}</h2>
              <p className="text-blue-100">@{user?.account}</p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${roleBadge.class}`}>
                {roleBadge.text}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
            <div className="text-center">
              <p className="text-3xl font-bold">${user?.ntBalance?.toLocaleString() || 0}</p>
              <p className="text-sm text-blue-100">新臺幣餘額</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold">
                {user?.msBalance?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || 0}
              </p>
              <p className="text-sm text-blue-100">數資幣餘額</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex p-1 bg-slate-100 rounded-xl mb-6">
        <button
          onClick={() => setActiveTab('info')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'info'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <User className="h-5 w-5" />
          基本資料
        </button>
        <button
          onClick={() => setActiveTab('password')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'password'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Lock className="h-5 w-5" />
          修改密碼
        </button>
      </div>

      {/* Forms */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
        <div className="p-6">
          {activeTab === 'info' ? (
            <form onSubmit={handleUpdateInfo} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <CreditCard className="inline h-4 w-4 mr-2" />
                  帳號
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500"
                  value={user?.account}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <User className="inline h-4 w-4 mr-2" />
                  姓名
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Phone className="inline h-4 w-4 mr-2" />
                  電話
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="請輸入電話號碼"
                />
              </div>

              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50"
              >
                {updateMutation.isPending ? '更新中...' : '更新資料'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">目前密碼</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">新密碼</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <p className="text-xs text-slate-400 mt-1">至少6個字元</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">確認新密碼</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 transition-all disabled:opacity-50"
              >
                {changePasswordMutation.isPending ? '更改中...' : '更改密碼'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
